import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { LANGUAGES } from "@/lib/constants";
import CampaignTypeSelect from "@/components/CampaignTypeSelect";
import SmartBriefBuilder from "@/components/SmartBriefBuilder";

const FOUNDATION_LABELS = {
    competitor_analysis: "Competitor",
    brand_dna: "Brand DNA",
    research: "Research",
    business_plan: "Business Plan",
};

export default function ProjectNew() {
    const [params] = useSearchParams();
    const presetClient = params.get("client_id") || "";
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState({
        client_id: presetClient,
        title: "",
        project_type: "brand_promotion_video",
        brief: "",
        language: "english",
        deadline: "",
        duration: "30s",
        video_platform: "google_flow",
        global_style_lock: {
            lighting: "", tone: "", camera_style: "", color_palette: "",
        },
        visual_lock: {
            character_identity: "", environment: "", visual_style: "",
        },
    });
    const [autoFilled, setAutoFilled] = useState(new Set());
    const [draftClientName, setDraftClientName] = useState("");
    const [busy, setBusy] = useState(false);
    const [foundation, setFoundation] = useState(null);  // null = not loaded, {available:{}} = none
    const [reuseChoice, setReuseChoice] = useState("auto"); // "auto" | "fresh"
    const nav = useNavigate();

    useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);

    // Whenever the selected client changes, fetch its existing foundation.
    useEffect(() => {
        if (!form.client_id) { setFoundation(null); return; }
        let cancelled = false;
        setFoundation(null);
        api.get(`/clients/${form.client_id}/foundation`)
            .then(({ data }) => { if (!cancelled) setFoundation(data); })
            .catch(() => { if (!cancelled) setFoundation({ available: {} }); });
        return () => { cancelled = true; };
    }, [form.client_id]);

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        if (autoFilled.has(k)) {
            setAutoFilled(prev => {
                const n = new Set(prev);
                n.delete(k);
                return n;
            });
        }
    };

    const submit = async () => {
        if (!form.client_id) { toast.error("Pick a client"); return; }
        if (!form.title) { toast.error("Title required"); return; }
        if (!form.brief) { toast.error("Brief required"); return; }
        setBusy(true);
        try {
            const approvedMap = (foundation && (foundation.approved_reusable_stages || foundation.available)) || {};
            const reusable = Object.keys(approvedMap).length > 0;
            // Strip Sprint B's UI-only sidecar fields before POST.
            const { __pending_competitors, __pending_social_links, ...payload } = form;
            if (reusable && reuseChoice === "auto") {
                payload.reuse_from_project_id = foundation.source_project_id;
            }
            const { data } = await api.post("/projects", payload);

            // Sprint B — if SmartBriefBuilder forwarded structured
            // competitor or social rows that the user confirmed, push
            // them through /api/intake/apply now that the project
            // exists. Per-row confirmation is preserved.
            if ((__pending_competitors?.length || 0) > 0 || (__pending_social_links?.length || 0) > 0) {
                try {
                    const confirmedMap = {};
                    (__pending_competitors || []).forEach((_, idx) => {
                        confirmedMap[`competitor.${idx}`] = true;
                    });
                    (__pending_social_links || []).forEach((_, idx) => {
                        confirmedMap[`social.${idx}`] = true;
                    });
                    await api.post("/intake/apply", {
                        target_client_id: form.client_id,
                        target_project_id: data.id,
                        client: {},
                        project: {},
                        competitors: __pending_competitors || [],
                        social_links: __pending_social_links || [],
                        confirmed: confirmedMap,
                    });
                } catch (e) {
                    // Non-fatal: project is created. User can re-run
                    // intake on the board.
                    console.warn("Sprint B intake/apply failed", e);
                }
            }

            toast.success(reusable && reuseChoice === "auto"
                ? "Project created. Only APPROVED foundation stages were reused."
                : "Project created. Routing to project board.");
            nav(`/projects/${data.id}`);
        } catch (e) {
            if (e?.response?.status !== 402) {
                toast.error(e?.userMessage || "Failed");
            }
        } finally { setBusy(false); }
    };

    const handleApplyDraft = (draft) => {
        const proj = draft?.project || {};
        const cli = draft?.client || {};
        const next = { ...form };
        const filled = new Set(autoFilled);

        if (proj.title) { next.title = proj.title; filled.add("title"); }
        if (proj.project_type) { next.project_type = proj.project_type; filled.add("project_type"); }
        if (proj.language) {
            const l = LANGUAGES.find(x => x.value === proj.language);
            if (l) { next.language = proj.language; filled.add("language"); }
        }
        if (proj.deadline) { next.deadline = proj.deadline; filled.add("deadline"); }

        // Sprint B — fold confirmed structured intake into project.brief
        // AS labelled sections, AND attach the machine-readable
        // intake_context blob the project create-payload forwards to
        // the backend (where it lives on Project.intake_context and is
        // consumed by every strategic agent via _format_intake_context).
        const INTAKE_KEYS = [
            ["campaign_goal", "Campaign Goal"],
            ["offer", "Offer"],
            ["cta", "CTA"],
            ["desired_output", "Desired Output"],
            ["platform_needs", "Platform Needs"],
            ["mood_tone", "Mood / Tone"],
            ["visual_direction", "Visual Direction"],
            ["mandatory_elements", "Mandatory Elements"],
            ["things_to_avoid", "Things To Avoid"],
            ["competitor_context", "Competitor Context"],
        ];
        const intakeCtx = {};
        const sections = [];
        INTAKE_KEYS.forEach(([k, label]) => {
            const v = proj[k];
            if (v && (typeof v !== "string" || v.trim())) {
                intakeCtx[k] = v;
                sections.push(`${label}:\n${typeof v === "string" ? v.trim() : Array.isArray(v) ? v.join(", ") : String(v)}`);
            }
        });

        // Combine LLM brief + structured sections under a clearly-marked block.
        let mergedBrief = (proj.brief || "").trim();
        if (sections.length > 0) {
            const block = `<!--FURMAIS_INTAKE_BRIEF-->\n=== CEO INTAKE (confirmed) ===\n\n${sections.join("\n\n")}\n<!--/FURMAIS_INTAKE_BRIEF-->`;
            mergedBrief = mergedBrief ? `${mergedBrief}\n\n${block}` : block;
        }
        if (mergedBrief) {
            next.brief = mergedBrief;
            filled.add("brief");
        }
        if (Object.keys(intakeCtx).length > 0) {
            next.intake_context = intakeCtx;
            filled.add("intake_context");
        }

        // Sprint B — confirmed competitors from the draft become
        // competitor_seeds on project creation. The richer per-competitor
        // structured rows are forwarded to /api/intake/apply by the parent
        // form after project creation completes (see submit() below).
        if (Array.isArray(draft?.competitors) && draft.competitors.length > 0) {
            const names = draft.competitors.map((c) => c?.name).filter(Boolean).slice(0, 12);
            if (names.length > 0) {
                next.competitor_seeds = names;
                next.__pending_competitors = draft.competitors;
                filled.add("competitor_seeds");
            }
        }
        // Forward confirmed social links so submit() can sync them via /intake/apply.
        if (Array.isArray(draft?.social_links) && draft.social_links.length > 0) {
            next.__pending_social_links = draft.social_links;
        }

        if (cli.company_name) {
            setDraftClientName(cli.company_name);
            const match = clients.find(c => (c.company_name || "").toLowerCase().trim() === cli.company_name.toLowerCase().trim());
            if (match) {
                next.client_id = match.id;
                filled.add("client_id");
            }
        }

        setForm(next);
        setAutoFilled(filled);

        setTimeout(() => {
            document.getElementById("project-form-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    const inputCls = (key) =>
        `w-full bg-black border rounded-sm px-3 py-2.5 outline-none ${
            autoFilled.has(key)
                ? "border-orange-500/50 focus:border-orange-400"
                : "border-zinc-800 focus:border-zinc-500"
        }`;

    const fieldLabel = (label, key) => (
        <label className="label-micro block mb-2 flex items-center gap-2">
            <span>{label}</span>
            {autoFilled.has(key) && (
                <span className="text-[9px] uppercase tracking-wider text-orange-400 border border-orange-500/40 px-1.5 py-0.5 rounded-sm">
                    AI filled
                </span>
            )}
        </label>
    );

    // QA Hardening — bucket-aware reuse flags from the new
    // ``approved_reusable_stages`` / ``output_present_but_not_approved``
    // surface served by /clients/{id}/foundation. We only treat
    // APPROVED stages as reusable; output-present-but-unapproved are
    // surfaced separately as a warning.
    const approvedReusable = (foundation && (foundation.approved_reusable_stages || foundation.available)) || {};
    const outputUnapproved = (foundation && foundation.output_present_but_not_approved) || {};
    const failedOrDegraded = (foundation && foundation.failed_or_degraded_stages) || {};
    const reusable = Object.keys(approvedReusable).length > 0;
    const canStartAtConcepts = !!(foundation && (foundation.can_start_at_concepts ?? reusable));
    const earliestMissing = (foundation && foundation.earliest_missing_required_stage) || null;

    return (
        <div className="p-6 sm:p-8 lg:p-10 max-w-3xl mx-auto" data-testid="project-new-page">
            <div className="mb-8">
                <div className="label-micro mb-2">New Project</div>
                <h1 className="font-display text-4xl font-medium tracking-tight">Brief the CEO Agent</h1>
                <p className="text-zinc-500 text-sm mt-2">
                    Chat your brief in or fill the form directly. Either way, the CEO Agent routes the
                    work through the agent pipeline once you create the project.
                </p>
            </div>

            <SmartBriefBuilder
                context="project"
                onApply={handleApplyDraft}
                title="Paste your campaign brief, references, or pasted analysis."
                placeholder="Paste your campaign brief, deck text, founder note, or anything else describing the project. The CEO will extract title, brief, campaign goal, offer, CTA, mood, visual direction, competitors, etc. — and let you confirm each field."
            />

            {draftClientName && !form.client_id && (
                <div data-testid="brief-client-hint"
                    className="mb-4 text-xs text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded-sm px-3 py-2">
                    Detected client <span className="font-medium">{draftClientName}</span> — create this
                    client first via <a className="underline" href="/clients/new">Add Client</a>, then come
                    back to link it here.
                </div>
            )}

            {reusable && (
                <div className="mb-6 card-base p-5 ceo-glow" data-testid="foundation-reuse-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="label-micro mb-1">Existing client foundation</div>
                            <h3 className="font-display text-lg tracking-tight">
                                Skip the strategy rerun for this client.
                            </h3>
                            <p className="text-xs text-zinc-500 mt-1">
                                We found previous APPROVED output from <span className="text-zinc-300">{foundation.source_project_title}</span>.
                                Only approved foundation stages will be reused — unapproved drafts will NOT be copied.
                            </p>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-800 px-2 py-1 rounded-sm">
                            {Object.keys(approvedReusable).length}/4 approved &amp; reusable
                        </span>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-4" data-testid="foundation-approved-chips">
                        {Object.entries(approvedReusable).map(([k, v]) => (
                            <span key={k} data-testid={`foundation-chip-${k}`}
                                className="text-[11px] border border-emerald-500/30 bg-emerald-500/5 text-emerald-200 rounded-sm px-2 py-1">
                                {FOUNDATION_LABELS[k] || k} · approved
                            </span>
                        ))}
                    </div>

                    {Object.keys(outputUnapproved).length > 0 && (
                        <div
                            className="mt-3 border border-amber-500/30 bg-amber-500/5 rounded-sm px-3 py-2"
                            data-testid="foundation-unapproved-warning"
                        >
                            <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-1">
                                Output exists but not approved — will not be reused
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(outputUnapproved).map(([k, v]) => (
                                    <span
                                        key={k}
                                        data-testid={`foundation-unapproved-chip-${k}`}
                                        className="text-[11px] border border-amber-500/30 bg-amber-500/[0.07] text-amber-200 rounded-sm px-2 py-1"
                                    >
                                        {FOUNDATION_LABELS[k] || k} · {v.source_status || "draft"}
                                    </span>
                                ))}
                            </div>
                            <p className="text-[11px] text-amber-200/80 mt-1.5">
                                {Object.values(outputUnapproved)[0]?.reason ||
                                    "Output exists but was not approved, so it will not be reused."}
                            </p>
                        </div>
                    )}

                    {Object.keys(failedOrDegraded).length > 0 && (
                        <div
                            className="mt-3 border border-rose-500/30 bg-rose-500/5 rounded-sm px-3 py-2"
                            data-testid="foundation-failed-warning"
                        >
                            <div className="text-[11px] uppercase tracking-wider text-rose-300 mb-1">
                                Failed / degraded source — not safe to reuse
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(failedOrDegraded).map(([k, v]) => (
                                    <span
                                        key={k}
                                        className="text-[11px] border border-rose-500/30 bg-rose-500/[0.07] text-rose-200 rounded-sm px-2 py-1"
                                    >
                                        {FOUNDATION_LABELS[k] || k} · {v.source_status || "failed"}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-4 flex gap-2 flex-wrap">
                        <label
                            className={`flex items-center gap-2 text-xs px-3 py-2 border rounded-sm ${
                                canStartAtConcepts ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                            } ${
                                reuseChoice === "auto" && canStartAtConcepts
                                    ? "border-orange-500/60 bg-orange-500/10 text-orange-100"
                                    : "border-zinc-800 text-zinc-400"
                            }`}
                            data-testid="reuse-choice-auto-label"
                            title={canStartAtConcepts
                                ? ""
                                : `Required approved foundation missing${earliestMissing ? ` (starts at ${earliestMissing.replace(/_/g, " ")})` : ""}.`}
                        >
                            <input type="radio" name="reuse" value="auto"
                                checked={reuseChoice === "auto" && canStartAtConcepts}
                                disabled={!canStartAtConcepts}
                                onChange={() => setReuseChoice("auto")}
                                className="accent-[#FF4400]"
                                data-testid="reuse-choice-auto" />
                            Reuse foundation · start at Concepts (recommended)
                        </label>
                        <label className={`flex items-center gap-2 text-xs px-3 py-2 border rounded-sm cursor-pointer ${
                            reuseChoice === "fresh" ? "border-zinc-500 bg-zinc-800/40 text-zinc-100" : "border-zinc-800 text-zinc-400"
                        }`}>
                            <input type="radio" name="reuse" value="fresh" checked={reuseChoice === "fresh"}
                                onChange={() => setReuseChoice("fresh")} className="accent-[#FF4400]"
                                data-testid="reuse-choice-fresh" />
                            Force full rerun from Brand DNA
                        </label>
                    </div>

                    {!canStartAtConcepts && (
                        <div className="text-[10px] text-amber-300 mt-2" data-testid="foundation-cannot-start-at-concepts">
                            Approved foundation is insufficient to start at Concepts
                            {earliestMissing
                                ? ` — earliest required stage missing: ${earliestMissing.replace(/_/g, " ")}.`
                                : "."}
                        </div>
                    )}

                    <div className="text-[10px] text-zinc-500 mt-3">
                        Only APPROVED stages from the source project are reused. The source project's outputs remain untouched.
                    </div>
                </div>
            )}

            <div id="project-form-section" className="card-base p-6 space-y-5">
                <div>
                    {fieldLabel("Client *", "client_id")}
                    <select
                        data-testid="proj-client"
                        value={form.client_id} onChange={e => set("client_id", e.target.value)}
                        className={inputCls("client_id")}>
                        <option value="">— pick a client —</option>
                        {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                    </select>
                </div>
                <div>
                    {fieldLabel("Campaign Type *", "project_type")}
                    <CampaignTypeSelect
                        testid="proj-type"
                        value={form.project_type}
                        onChange={v => set("project_type", v)}
                    />
                </div>
                <div>
                    {fieldLabel("Project Title *", "title")}
                    <input
                        data-testid="proj-title"
                        value={form.title} onChange={e => set("title", e.target.value)}
                        placeholder="e.g. Diwali Launch Film for Aurum Watches"
                        className={inputCls("title")}
                    />
                </div>
                <div>
                    {fieldLabel("Brief / Idea *", "brief")}
                    <textarea
                        data-testid="proj-brief"
                        rows={6}
                        value={form.brief} onChange={e => set("brief", e.target.value)}
                        placeholder="Describe the campaign, the ask, mood, references…"
                        className={`${inputCls("brief")} resize-y`}
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        {fieldLabel("Language", "language")}
                        <select
                            data-testid="proj-language"
                            value={form.language} onChange={e => set("language", e.target.value)}
                            className={inputCls("language")}>
                            {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </select>
                    </div>
                    <div>
                        {fieldLabel("Deadline", "deadline")}
                        <input
                            data-testid="proj-deadline"
                            value={form.deadline} onChange={e => set("deadline", e.target.value)}
                            placeholder="e.g. 25 Nov 2026"
                            className={inputCls("deadline")}
                        />
                    </div>
                </div>

                {/* Phase I — Video production controls */}
                <div className="pt-3 border-t border-zinc-900">
                    <div className="label-micro mb-3">Video production controls</div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Duration</label>
                            <select
                                data-testid="proj-duration"
                                value={form.duration} onChange={e => set("duration", e.target.value)}
                                className={inputCls("duration")}>
                                <option value="15s">15 sec</option>
                                <option value="30s">30 sec</option>
                                <option value="60s">60 sec</option>
                                <option value="custom">Custom…</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Video platform</label>
                            <select
                                data-testid="proj-platform"
                                value={form.video_platform} onChange={e => set("video_platform", e.target.value)}
                                className={inputCls("video_platform")}>
                                <option value="google_flow">Google Flow (recommended)</option>
                                <option value="runway">Runway (future)</option>
                                <option value="hixfield">Hixfield (future)</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div>
                    <div className="label-micro mb-3">Visual lock · keep identity consistent across scenes</div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Character identity</label>
                            <select
                                data-testid="proj-char"
                                value={form.visual_lock.character_identity}
                                onChange={e => set("visual_lock", { ...form.visual_lock, character_identity: e.target.value })}
                                className={inputCls("visual_lock")}>
                                <option value="">— any —</option>
                                <option value="Indian">Indian</option>
                                <option value="Western">Western</option>
                                <option value="African">African</option>
                                <option value="Mixed">Mixed</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Environment</label>
                            <select
                                data-testid="proj-env"
                                value={form.visual_lock.environment}
                                onChange={e => set("visual_lock", { ...form.visual_lock, environment: e.target.value })}
                                className={inputCls("visual_lock")}>
                                <option value="">— any —</option>
                                <option value="Indian">Indian</option>
                                <option value="American">American</option>
                                <option value="European">European</option>
                                <option value="Premium">Premium</option>
                                <option value="Custom">Custom</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Visual style</label>
                            <select
                                data-testid="proj-style"
                                value={form.visual_lock.visual_style}
                                onChange={e => set("visual_lock", { ...form.visual_lock, visual_style: e.target.value })}
                                className={inputCls("visual_lock")}>
                                <option value="">— default —</option>
                                <option value="Cinematic">Cinematic</option>
                                <option value="Premium Ad">Premium Ad</option>
                                <option value="Documentary">Documentary</option>
                                <option value="Social">Social</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div>
                    <div className="label-micro mb-3">Global style lock · applied to every prompt</div>
                    <div className="grid grid-cols-2 gap-3">
                        <input
                            data-testid="proj-lighting"
                            placeholder="Lighting (e.g. golden-hour warm)"
                            value={form.global_style_lock.lighting}
                            onChange={e => set("global_style_lock", { ...form.global_style_lock, lighting: e.target.value })}
                            className={inputCls("global_style_lock")}
                        />
                        <input
                            data-testid="proj-tone"
                            placeholder="Tone (e.g. aspirational, confident)"
                            value={form.global_style_lock.tone}
                            onChange={e => set("global_style_lock", { ...form.global_style_lock, tone: e.target.value })}
                            className={inputCls("global_style_lock")}
                        />
                        <input
                            data-testid="proj-camera"
                            placeholder="Camera style (e.g. 35mm shallow DOF)"
                            value={form.global_style_lock.camera_style}
                            onChange={e => set("global_style_lock", { ...form.global_style_lock, camera_style: e.target.value })}
                            className={inputCls("global_style_lock")}
                        />
                        <input
                            data-testid="proj-palette"
                            placeholder="Color palette (e.g. warm amber + cream)"
                            value={form.global_style_lock.color_palette}
                            onChange={e => set("global_style_lock", { ...form.global_style_lock, color_palette: e.target.value })}
                            className={inputCls("global_style_lock")}
                        />
                    </div>
                </div>
            </div>
            <div className="flex justify-end mt-6">
                <button data-testid="proj-create" onClick={submit} disabled={busy} className="btn-primary">
                    {busy ? "Creating…" : "Create project"}
                </button>
            </div>
        </div>
    );
}
